import { useQuery } from "@tanstack/react-query";
import { Alan, Secim } from "@koclukpanel/ui";
import { ogrenciKaynaklari } from "./api";
import type { IcerikDegeri, IcerikHatalari } from "./gorev-icerigi";

/**
 * GÖREVİN KAYNAK VE BAĞLANTI ALANLARI — F-SCH-03 / F-SCH-03b
 *
 * Müşteri revizesi (04.10.2026): koç program hazırlarken öğrencinin
 * kaynak kitaplarını, YouTube bağlantısını, konu PDF'ini ve test PDF'ini
 * aynı ekranda girebilmeli. Sunucu bu alanların çoğunu baştan kabul
 * ediyordu; formda yoklardı ve öğrencinin "Konu PDF" düğmesi bu yüzden
 * hiçbir görevde çıkmıyordu.
 *
 * ================================================================
 * KAYNAK LİSTESİ SEÇİLEN DERSE GÖRE SÜZÜLÜYOR
 * ================================================================
 * Öğrencinin listesi bütün derslerin kitaplarını içeriyor; "TYT
 * Matematik" görevine Fizik kitabı bağlamak bir yanlış tıklama. Görevde
 * ZATEN seçili olan kaynak, dersi farklı olsa bile listede kalıyor:
 * kaybolsaydı form onu sessizce boşaltır, kaydedilince kaynak silinirdi.
 */
export function GorevIcerikAlanlari({
  ogrenciId,
  dersId,
  deger,
  onChange,
  hatalar,
}: {
  ogrenciId: string;
  dersId: string;
  deger: IcerikDegeri;
  onChange: (d: IcerikDegeri) => void;
  hatalar: IcerikHatalari;
}) {
  const kaynaklar = useQuery({
    queryKey: ["ogrenci-kaynaklari", ogrenciId],
    queryFn: () => ogrenciKaynaklari(ogrenciId),
    retry: false,
  });
  const yaz = (k: keyof IcerikDegeri, v: string) => onChange({ ...deger, [k]: v });

  const liste = (kaynaklar.data ?? []).filter(
    (k) => k.dersId === dersId || k.id === deger.kaynakId,
  );

  return (
    <>
      {/*
        `min-w-0` ŞART: `<fieldset>`in tarayıcı varsayılanı `min-inline-size:
        min-content`. Uzun bir kitap adı açılır listeyi içerik genişliğine
        itiyor ve 390 px'de kutu ekrandan taşıp oku kayboluyordu (ölçüldü).
      */}
      <fieldset className="flex min-w-0 flex-col gap-4">
        <legend className="text-notr-900 mb-2 text-sm font-semibold">Kaynak ve hedef</legend>
        <Secim
          etiket="Kaynak kitap"
          value={deger.kaynakId}
          onChange={(e) => yaz("kaynakId", e.target.value)}
          disabled={!dersId || kaynaklar.isPending}
          hata={
            kaynaklar.isError
              ? "Öğrencinin kaynak listesi okunamadı; bu, kaynağı olmadığı anlamına gelmez."
              : undefined
          }
          yardim={
            kaynaklar.isSuccess && dersId && liste.length === 0
              ? "Bu ders için öğrencinin listesinde kitap yok. Öğrenci dosyası → Kaynaklar'dan eklenebilir."
              : "Öğrencinin bu derse ait kitapları."
          }
        >
          <option value="">Kaynak yok</option>
          {liste.map((k) => (
            <option key={k.id} value={k.id}>
              {k.yayinevi ? `${k.ad} — ${k.yayinevi}` : k.ad}
            </option>
          ))}
        </Secim>
        <Alan
          etiket="Hedef test sayısı"
          type="number"
          min={1}
          value={deger.testHedefi}
          onChange={(e) => yaz("testHedefi", e.target.value)}
          hata={hatalar.testHedefi}
          yardim="İsteğe bağlı."
        />
      </fieldset>

      <fieldset className="flex min-w-0 flex-col gap-4">
        <legend className="text-notr-900 mb-2 text-sm font-semibold">Video ve PDF</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          <Alan
            etiket="YouTube bağlantısı"
            type="url"
            inputMode="url"
            placeholder="https://youtu.be/…"
            value={deger.youtube}
            onChange={(e) => yaz("youtube", e.target.value)}
            hata={hatalar.youtube}
          />
          <Alan
            etiket="Video başlığı"
            value={deger.videoBasligi}
            onChange={(e) => yaz("videoBasligi", e.target.value)}
            hata={hatalar.videoBasligi}
            yardim="Öğrencinin düğmesinde bu yazar; boşsa “Video”."
          />
          <Alan
            etiket="Konu PDF bağlantısı"
            type="url"
            inputMode="url"
            placeholder="https://drive.google.com/…"
            value={deger.konuPdf}
            onChange={(e) => yaz("konuPdf", e.target.value)}
            hata={hatalar.konuPdf}
          />
          <Alan
            etiket="Test PDF bağlantısı"
            type="url"
            inputMode="url"
            placeholder="https://drive.google.com/…"
            value={deger.testPdf}
            onChange={(e) => yaz("testPdf", e.target.value)}
            hata={hatalar.testPdf}
          />
        </div>
        <p className="text-notr-600 text-xs">
          Drive bağlantısının “bağlantıya sahip olan herkes görüntüleyebilir” paylaşımında olması
          gerekir; yoksa öğrenci dosyayı açamaz.
        </p>
      </fieldset>
    </>
  );
}
